import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildScheduleClipCalendarUrl,
  fetchOneTapPreferences,
  hasValidOneTapPreferences,
  isClipOneTapScheduled,
  markClipOneTapScheduled,
  oneTapPublishClip,
  oneTapScheduledClipKey,
} from './oneTapSchedule';
import { trackOneTapPublishClick } from './userJourneyTracker';
import './ResultPublishButton.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const SOCIAL_ICONS = [
  { src: `${PUBLIC_URL}/instagram.svg`, alt: 'Instagram' },
  { src: `${PUBLIC_URL}/YouTube.svg`, alt: 'YouTube' },
  { src: `${PUBLIC_URL}/linkedin.svg`, alt: 'LinkedIn' },
  { src: `${PUBLIC_URL}/Facebook.png`, alt: 'Facebook' },
];

function RocketIcon() {
  return (
    <svg
      className="result-publish-bar__rocket"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ScheduledIcon() {
  return (
    <svg
      className="result-publish-bar__scheduled-icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ResultPublishButton({
  fileStem = '',
  clipFilename = '',
  taskId = null,
  userId = '',
  isPremiumUser = false,
  getAuthToken,
  onStatusMessage,
  hideIcons = false,
}) {
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const clipKey = useMemo(
    () => oneTapScheduledClipKey({ fileStem, clipFilename, taskId }),
    [fileStem, clipFilename, taskId],
  );
  const [scheduled, setScheduled] = useState(() => (
    isClipOneTapScheduled(userId, { fileStem, clipFilename, taskId })
  ));

  useEffect(() => {
    setScheduled(isClipOneTapScheduled(userId, { fileStem, clipFilename, taskId }));
  }, [userId, fileStem, clipFilename, taskId, clipKey]);

  const openScheduleDialogForClip = () => {
    navigate(buildScheduleClipCalendarUrl({ fileStem, clipFilename, taskId }));
  };

  const handleClick = async () => {
    if (scheduled || publishing) return;
    trackOneTapPublishClick({
      file_stem: fileStem || undefined,
      clip_filename: clipFilename || undefined,
      task_id: taskId || undefined,
    });
    if (!fileStem || !clipFilename) {
      onStatusMessage?.('Could not publish this clip yet. Try again in a moment.');
      return;
    }
    if (!getAuthToken) {
      onStatusMessage?.('Please sign in to publish.');
      return;
    }

    setPublishing(true);
    onStatusMessage?.(null);

    try {
      const preferences = await fetchOneTapPreferences(getAuthToken, userId);
      if (!hasValidOneTapPreferences(preferences)) {
        openScheduleDialogForClip();
        return;
      }

      const result = await oneTapPublishClip(getAuthToken, {
        fileStem,
        clipFilename,
        taskId,
      });
      const when = result.scheduled_at
        ? new Date(result.scheduled_at).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
        : 'your next open slot';
      const platforms = (result.created || []).map((entry) => entry.platform).join(', ');
      onStatusMessage?.(
        platforms
          ? `Scheduled on ${platforms} for ${when}.`
          : `Scheduled for ${when}.`,
      );
      markClipOneTapScheduled(userId, clipKey);
      setScheduled(true);
    } catch (error) {
      if (
        error.code === 'preferences_missing'
        || error.code === 'preferences_disabled'
        || error.message?.includes('preferences')
      ) {
        openScheduleDialogForClip();
        return;
      }
      onStatusMessage?.(error.message || '1-Tap publish failed.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <button
      type="button"
      className={[
        'result-publish-bar',
        scheduled && 'result-publish-bar--scheduled',
        hideIcons && 'result-publish-bar--no-icons',
      ].filter(Boolean).join(' ')}
      aria-label={scheduled ? 'Scheduled' : '1-Tap Publish'}
      disabled={publishing || scheduled}
      aria-busy={publishing}
      onClick={handleClick}
    >
      <span className="result-publish-bar__left">
        {!hideIcons && (scheduled ? <ScheduledIcon /> : <RocketIcon />)}
        <span className="result-publish-bar__label">
          {scheduled ? 'Scheduled' : publishing ? 'Scheduling…' : '1-Tap Publish'}
        </span>
      </span>
      {!scheduled && !hideIcons && (
        <>
          <span className="result-publish-bar__divider" aria-hidden="true" />
          <span className="result-publish-bar__icons">
            {SOCIAL_ICONS.map(({ src, alt }) => (
              <img
                key={alt}
                src={src}
                alt=""
                className="result-publish-bar__icon"
                width={14}
                height={14}
                aria-hidden="true"
              />
            ))}
          </span>
        </>
      )}
    </button>
  );
}
